import { Box } from "@mui/material";
import Link from "next/link";
import { Fragment, type ButtonHTMLAttributes, type ReactNode } from "react";
import { SiteButtonSpinner } from "../Home/siteKit/SiteButtonSpinner";
import { AUTH_CARD_STYLES } from "./authCardStyles";

export interface AuthSubmitButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** 요청 중 — 글자 앞에 진행 표시를 두고, 다시 누르지 못하게 막고, 보조 기술에 알린다(흐리게 하지 않는다) */
  loading?: boolean;
  /** 칸이 많은 단계(회원 정보)에서 위 간격을 줄인다 */
  tight?: boolean;
  children: ReactNode;
}

/** 카드의 주 버튼 — 폼 제출(Enter 와 같다) */
export function AuthSubmitButton({
  loading = false,
  tight = false,
  disabled,
  children,
  ...buttonProps
}: AuthSubmitButtonProps) {
  return (
    <Box
      component="button"
      type="submit"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-auth-part="submit"
      data-tight={tight ? "" : undefined}
      sx={AUTH_CARD_STYLES.submit}
      {...buttonProps}
    >
      {loading ? <SiteButtonSpinner /> : null}
      {children}
    </Box>
  );
}

export interface AuthSideButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** 요청 중 — 글자 앞에 진행 표시를 두고, 다시 누르지 못하게 막고, 보조 기술에 알린다(회색 비활성 모양으로 바꾸지 않는다) */
  loading?: boolean;
  children: ReactNode;
}

/** 칸 옆 보조 단추(중복확인) */
export function AuthSideButton({
  loading = false,
  disabled,
  children,
  ...buttonProps
}: AuthSideButtonProps) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      sx={AUTH_CARD_STYLES.sideButton}
      {...buttonProps}
    >
      {loading ? <SiteButtonSpinner /> : null}
      {children}
    </Box>
  );
}

/** 보조 링크 줄 항목 — 다른 화면으로 가면 `href`(링크), 같은 화면 안 동작(재전송·이전 단계)이면 `onClick`(단추) */
export type AuthLinkItem = { label: string; testId?: string } & (
  | { href: string }
  | { onClick: () => void; disabled?: boolean }
);

/** 카드 아래 보조 링크 줄(회원가입 | 비밀번호 찾기 등) — 사이는 가는 세로선 */
export function AuthLinks({ items }: { items: readonly AuthLinkItem[] }) {
  return (
    <Box data-auth-part="links" sx={AUTH_CARD_STYLES.links}>
      {items.map((item, index) => (
        <Fragment key={item.label}>
          {index > 0 ? <i aria-hidden="true" /> : null}
          {"href" in item ? (
            <Link href={item.href} data-testid={item.testId}>
              {item.label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={item.onClick}
              disabled={item.disabled}
              data-testid={item.testId}
            >
              {item.label}
            </button>
          )}
        </Fragment>
      ))}
    </Box>
  );
}
